/**
 * Hooks local-first de EVALUACIÓN (F3-B): tareas, gradebook (categorías/ítems),
 * mis entregas y notas, y el roster de entregas/notas de una cohorte (profesor).
 */
import type { Accessor } from "solid-js";
import {
  assessmentQueryKeys,
  getAssignmentQuery,
  getCohortGradesQuery,
  getCohortSubmissionsQuery,
  getCourseAssignmentsQuery,
  getGradeCategoriesQuery,
  getGradeItemsQuery,
  getMyActiveCohortForCourseQuery,
  getMyGradesQuery,
  getMySubmissionQuery,
  getMySubmissionsQuery,
} from "@space/shared";
import { createPowerSyncQuery, createPowerSyncSingle } from "@space/shared/solid";
import type {
  Assignment,
  AssignmentGrade,
  AssignmentSubmission,
  GradeCategory,
  GradeItem,
} from "@space/powersync";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

function useBase() {
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  return { db, drizzle, staleTime: OFFLINE_CACHE_STALE_TIME_MS };
}

export function useCourseAssignments(courseId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<Assignment>({
    queryKey: () => assessmentQueryKeys.courseAssignments(courseId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = courseId();
      return db && id ? getCourseAssignmentsQuery(db, id) : null;
    },
    enabled: () => Boolean(courseId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useAssignment(assignmentId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncSingle<Assignment>({
    queryKey: () => assessmentQueryKeys.assignment(assignmentId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = assignmentId();
      return db && id ? getAssignmentQuery(db, id) : null;
    },
    enabled: () => Boolean(assignmentId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useGradeCategories(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<GradeCategory>({
    queryKey: () => assessmentQueryKeys.gradeCategories(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getGradeCategoriesQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useGradeItems(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<GradeItem>({
    queryKey: () => assessmentQueryKeys.gradeItems(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getGradeItemsQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useMySubmission(
  assignmentId: Accessor<string | undefined>,
  cohortId: Accessor<string | undefined>,
  userId: Accessor<string | undefined>,
) {
  const base = useBase();
  return createPowerSyncSingle<AssignmentSubmission>({
    queryKey: () => assessmentQueryKeys.mySubmission(assignmentId(), cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const a = assignmentId();
      const c = cohortId();
      const u = userId();
      return db && a && c && u ? getMySubmissionQuery(db, a, c, u) : null;
    },
    enabled: () =>
      Boolean(assignmentId()) &&
      Boolean(cohortId()) &&
      Boolean(userId()) &&
      Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useMySubmissions(userId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<AssignmentSubmission>({
    queryKey: () => assessmentQueryKeys.mySubmissions(userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = userId();
      return db && id ? getMySubmissionsQuery(db, id) : null;
    },
    enabled: () => Boolean(userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useMyGrades(userId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<AssignmentGrade>({
    queryKey: () => assessmentQueryKeys.myGrades(userId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = userId();
      return db && id ? getMyGradesQuery(db, id) : null;
    },
    enabled: () => Boolean(userId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useCohortSubmissions(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<AssignmentSubmission>({
    queryKey: () => assessmentQueryKeys.cohortSubmissions(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getCohortSubmissionsQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

/** MI cohorte activa para un curso (contexto de entrega del player). */
export function useMyActiveCohortForCourse(
  userId: Accessor<string | undefined>,
  courseId: Accessor<string | undefined>,
) {
  const base = useBase();
  return createPowerSyncSingle<{ cohort_id: string | null; organization_id: string | null }>({
    queryKey: () => assessmentQueryKeys.myCohortForCourse(userId(), courseId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const u = userId();
      const c = courseId();
      return db && u && c ? getMyActiveCohortForCourseQuery(db, u, c) : null;
    },
    enabled: () => Boolean(userId()) && Boolean(courseId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}

export function useCohortGrades(cohortId: Accessor<string | undefined>) {
  const base = useBase();
  return createPowerSyncQuery<AssignmentGrade>({
    queryKey: () => assessmentQueryKeys.cohortGrades(cohortId()),
    db: base.db,
    queryFn: () => {
      const db = base.drizzle();
      const id = cohortId();
      return db && id ? getCohortGradesQuery(db, id) : null;
    },
    enabled: () => Boolean(cohortId()) && Boolean(base.drizzle()),
    staleTime: base.staleTime,
    suspense: false,
  });
}
